import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlass, X, ArrowRight, CircleNotch, ClockCounterClockwise } from '@phosphor-icons/react';
import { lomAPI } from '../store/authStore';
import { getSearchHistory, addSearchTerm, clearSearchHistory, subscribeSearchHistory } from '../store/searchHistoryStore';

/**
 * GlobalSearchBar
 * ────────────────
 * Spotlight-style cross-LOM search. Debounced query hits
 * `/api/lom/search` (rapidfuzz on the backend) and surfaces the top-8
 * results as a dropdown. `Enter` (or "See all …") navigates to
 * `/search?q=...` for the full grouped-by-LOM results view.
 *
 * Cmd/Ctrl+K opens the bar from anywhere; ESC closes.
 * Every interactive element carries a `data-testid` for automation.
 */
export default function GlobalSearchBar({ variant = 'compact', placeholder }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(-1);
  const [history, setHistory] = useState(getSearchHistory());

  // Keep the local `history` state in sync with the store so all
  // GlobalSearchBar instances refresh when a term is added elsewhere.
  useEffect(() => subscribeSearchHistory(setHistory), []);

  // Debounced search
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const runSearch = useCallback((term) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!term || term.trim().length < 2) {
      setResults([]); setTotal(0); setLoading(false);
      if (abortRef.current) abortRef.current.abort();
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        const { data } = await lomAPI.search(term, { limit: 12 });
        setResults(data.results || []);
        setTotal(data.total || 0);
        setSelected(-1);
      } catch (err) {
        if (err?.name !== 'CanceledError') console.warn('[search] failed:', err);
      } finally {
        setLoading(false);
      }
    }, 220);
  }, []);

  useEffect(() => {
    runSearch(q);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, runSearch]);

  // Cmd/Ctrl+K to focus, ESC to blur/close
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      } else if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const goToHit = useCallback((hit) => {
    setOpen(false);
    addSearchTerm(q);
    // Pass the query along so the destination page can auto-highlight the
    // matched L4 within the LOM browser.
    const sep = hit.route.includes('?') ? '&' : '?';
    navigate(`${hit.route}${sep}q=${encodeURIComponent(q)}&highlight=${encodeURIComponent(hit.id)}`);
  }, [navigate, q]);

  const goToAll = useCallback(() => {
    if (!q.trim()) return;
    setOpen(false);
    addSearchTerm(q);
    navigate(`/search?q=${encodeURIComponent(q)}`);
  }, [navigate, q]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected(s => Math.min(results.length - 1, s + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected(s => Math.max(-1, s - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selected >= 0 && results[selected]) goToHit(results[selected]);
      else goToAll();
    }
  };

  const levelBadge = (lvl) => ({
    background: lvl === 'L4' ? 'rgba(139,92,246,0.14)' : lvl === 'L3' ? 'rgba(46,112,173,0.14)' : lvl === 'L2' ? 'rgba(15,138,79,0.14)' : 'rgba(242,107,31,0.14)',
    color:      lvl === 'L4' ? '#7C3AED' : lvl === 'L3' ? '#1E4E8C' : lvl === 'L2' ? '#0F8A4F' : '#F26B1F',
  });

  const isCompact = variant === 'compact';
  const inputW = isCompact ? 260 : 480;

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: inputW }} data-testid="global-search-wrapper">
      <div style={{ position: 'relative' }}>
        <MagnifyingGlass size={14} weight="bold" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }} />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder || 'Search across all LOMs…'}
          data-testid="global-search-input"
          style={{
            width: '100%', padding: '7px 34px 7px 30px',
            background: 'var(--bg-2)', color: 'var(--text-1)',
            border: '1px solid var(--border-1)', borderRadius: 999,
            fontSize: 12.5, fontWeight: 500, outline: 'none',
            transition: 'border-color 0.15s, background 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
          onMouseLeave={(e) => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.borderColor = 'var(--border-1)'; }}
        />
        {q && !loading && (
          <button
            data-testid="global-search-clear"
            onClick={() => { setQ(''); inputRef.current?.focus(); }}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: 'var(--text-3)', cursor: 'pointer', padding: 2, borderRadius: 4, display: 'flex' }}
            title="Clear"
          >
            <X size={12} weight="bold" />
          </button>
        )}
        {loading && (
          <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--accent)' }}>
            <CircleNotch size={12} weight="bold" style={{ animation: 'spin 0.9s linear infinite' }} />
          </div>
        )}
        {isCompact && (
          <span style={{ position: 'absolute', right: 30, top: '50%', transform: 'translateY(-50%)', fontSize: 9, color: 'var(--text-3)', pointerEvents: 'none', fontFamily: 'monospace', padding: '1px 5px', borderRadius: 3, border: '1px solid var(--border-1)', background: 'var(--bg-1)' }}>⌘K</span>
        )}
      </div>

      {open && (q.trim().length >= 2 || (q.trim().length === 0 && history.length > 0)) && (
        <div
          data-testid="global-search-dropdown"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
            background: 'var(--bg-1)', border: '1px solid var(--border-1)',
            borderRadius: 10, boxShadow: '0 12px 36px -8px rgba(10,14,26,0.35)',
            zIndex: 80, overflow: 'hidden', maxHeight: 460, display: 'flex', flexDirection: 'column',
          }}
        >
          {q.trim().length < 2 && history.length > 0 && (
            <div data-testid="global-search-history" style={{ padding: '10px 12px 6px', borderBottom: '1px solid var(--border-1)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <ClockCounterClockwise size={11} weight="bold" style={{ color: 'var(--text-3)' }} />
                <span style={{ fontSize: 9, fontWeight: 800, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Recent searches</span>
                <button
                  data-testid="global-search-history-clear"
                  onMouseDown={(e) => { e.preventDefault(); clearSearchHistory(); }}
                  style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: 'var(--text-3)', fontSize: 9, cursor: 'pointer', fontWeight: 700, padding: 0 }}
                >
                  Clear
                </button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {history.map((term, i) => (
                  <button
                    key={term}
                    data-testid={`global-search-history-chip-${i}`}
                    onMouseDown={(e) => { e.preventDefault(); setQ(term); }}
                    style={{ padding: '3px 9px', borderRadius: 999, background: 'var(--bg-2)', border: '1px solid var(--border-1)', color: 'var(--text-2)', fontSize: 10.5, fontWeight: 600, cursor: 'pointer' }}
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          )}
          {q.trim().length >= 2 && results.length === 0 && !loading && (
            <div style={{ padding: '18px 16px', fontSize: 12, color: 'var(--text-3)', textAlign: 'center' }} data-testid="global-search-empty">
              No matches for <strong style={{ color: 'var(--text-1)' }}>&quot;{q}&quot;</strong>
            </div>
          )}
          {results.length > 0 && (
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {results.slice(0, 8).map((h, i) => (
                <button
                  key={`${h.lomId}-${h.id}-${i}`}
                  data-testid={`global-search-hit-${i}`}
                  onMouseDown={(e) => { e.preventDefault(); goToHit(h); }}
                  onMouseEnter={() => setSelected(i)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '10px 14px', border: 'none', borderBottom: '1px solid var(--border-1)',
                    background: selected === i ? 'var(--bg-2)' : 'transparent',
                    cursor: 'pointer', font: 'inherit', color: 'inherit',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 4, ...levelBadge(h.level), letterSpacing: '0.05em' }}>
                      {h.level}
                    </span>
                    <span style={{ fontSize: 10, color: 'var(--text-3)', fontWeight: 700 }}>{h.lomTitle}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 9, color: 'var(--text-3)', fontFamily: 'monospace' }}>{h.score}</span>
                  </div>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-1)', lineHeight: 1.35, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {h.title}
                  </div>
                  {Array.isArray(h.breadcrumb) && h.breadcrumb.length > 0 && (
                    <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {h.breadcrumb.slice(0, -1).join(' › ')}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
          {results.length > 0 && (
            <button
              data-testid="global-search-see-all"
              onMouseDown={(e) => { e.preventDefault(); goToAll(); }}
              style={{
                padding: '10px 14px', background: 'var(--bg-2)',
                border: 'none', borderTop: '1px solid var(--border-1)',
                cursor: 'pointer', color: 'var(--accent)', fontSize: 12, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
              }}
            >
              <span>See all {total} results for &quot;{q}&quot;</span>
              <ArrowRight size={12} weight="bold" />
            </button>
          )}
        </div>
      )}
      <style>{`@keyframes spin { to { transform: translateY(-50%) rotate(360deg); } }`}</style>
    </div>
  );
}
